import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneTilt, Trash, PlusCircle, MagicWand, Eraser, FileXls, FilePdf, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { api, apiError, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const CREW_PALETTE = ["#1E3A8A", "#059669", "#F59E0B", "#7C3AED", "#DC2626", "#0EA5E9", "#DB2777", "#65A30D"];
// Iter101: presencia según turno — D (día) · N (noche). El shift real se
// resuelve por celda vía data.rows[].attendance_shifts (fallback a shift_type).
const ATT_CELL = {
  present: { l: "D", cls: "bg-emerald-600 text-white font-bold" },
  present_night: { l: "N", cls: "bg-indigo-700 text-white font-bold" },
  absent: { l: "A", cls: "bg-red-500 text-white font-bold" },
  justified: { l: "J", cls: "bg-amber-500 text-white font-bold" },
  rest: { l: "Ds", cls: "bg-slate-600 text-white font-bold" },
  vacation: { l: "V", cls: "bg-blue-500 text-white font-bold" },
  permit: { l: "Pe", cls: "bg-purple-500 text-white font-bold" },
  medical: { l: "Md", cls: "bg-orange-500 text-white font-bold" },
};
const ATT_LIST = [
  { v: "present_day", l: "Presente · Día (D)", dot: "bg-emerald-600", status: "present", shift: "day" },
  { v: "present_night", l: "Presente · Noche (N)", dot: "bg-indigo-700", status: "present", shift: "night" },
  { v: "absent", l: "Ausente (A)", dot: "bg-red-500", status: "absent" },
  { v: "justified", l: "Justificado (J)", dot: "bg-amber-500", status: "justified" },
  { v: "rest", l: "Descanso (Ds)", dot: "bg-slate-600", status: "rest" },
  { v: "vacation", l: "Vacaciones (V)", dot: "bg-blue-500", status: "vacation" },
  { v: "permit", l: "Permiso (Pe)", dot: "bg-purple-500", status: "permit" },
  { v: "medical", l: "Médico (Md)", dot: "bg-orange-500", status: "medical" },
];

function ViaticosDialog() {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [newMail, setNewMail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/hr/viaticos-settings").then((r) => setCfg(r.data)).catch(() => {});
  }, [open]);

  const save = async () => {
    try {
      const { data } = await api.put("/hr/viaticos-settings", cfg);
      setCfg(data);
      toast.success("Configuración de viáticos guardada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const scanNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hr/viaticos/scan");
      if (data.found > 0) toast.success(`Recordatorio generado: ${data.found} colaborador(es) · correo: ${data.status}`);
      else toast.info(data.status === "deshabilitado" ? "Recordatorios deshabilitados" : "Nadie termina rol en la ventana configurada");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const addMail = () => {
    const m = newMail.trim().toLowerCase();
    if (!m || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(m)) { toast.error("Correo inválido"); return; }
    if (cfg.recipients.includes(m)) return;
    setCfg({ ...cfg, recipients: [...cfg.recipients, m] });
    setNewMail("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="viaticos-config-btn">
          <EnvelopeSimple size={14} weight="bold" /> Recordatorio de viáticos
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Recordatorio de pago de viáticos</DialogTitle></DialogHeader>
        {!cfg ? <p className="text-sm text-slate-400 py-6 text-center">Cargando…</p> : (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              Se enviará un correo automático a los destinatarios cuando falten <b>{cfg.days_before} día(s)</b> para
              que un colaborador termine su rol, recordando el pago de los viáticos de viaje.
            </p>
            <div className="flex items-center justify-between">
              <Label className="overline-label">Activo</Label>
              <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="viaticos-enabled" />
            </div>
            <div>
              <Label className="overline-label">Días antes del fin de rol</Label>
              <Select value={String(cfg.days_before)} onValueChange={(v) => setCfg({ ...cfg, days_before: Number(v) })}>
                <SelectTrigger className="h-9" data-testid="viaticos-days-before"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{[1, 2, 3, 4, 5].map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline-label">Destinatarios del correo</Label>
              <div className="flex gap-2 mt-1">
                <Input value={newMail} onChange={(e) => setNewMail(e.target.value)} placeholder="correo@empresa.com"
                       onKeyDown={(e) => e.key === "Enter" && addMail()} data-testid="viaticos-new-recipient" />
                <Button variant="outline" size="sm" onClick={addMail} className="shrink-0" data-testid="viaticos-add-recipient">
                  <PlusCircle size={14} weight="bold" />
                </Button>
              </div>
              <div className="mt-2 space-y-1" data-testid="viaticos-recipients-list">
                {cfg.recipients.map((m) => (
                  <div key={m} className="flex items-center justify-between bg-slate-50 rounded px-2 py-1 text-xs">
                    <span>{m}</span>
                    <button onClick={() => setCfg({ ...cfg, recipients: cfg.recipients.filter((x) => x !== m) })}
                            className="text-slate-400 hover:text-red-500"><Trash size={12} /></button>
                  </div>
                ))}
                {!cfg.recipients.length && <p className="text-[11px] text-slate-400 italic">Sin destinatarios configurados.</p>}
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={save} className="bg-blue-900 hover:bg-blue-800 flex-1" data-testid="viaticos-save">Guardar</Button>
              <Button variant="outline" onClick={scanNow} disabled={busy} className="gap-1.5" data-testid="viaticos-scan-now">
                <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Enviando…" : "Escanear ahora"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CalendarTab({ canManage }) {
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null); // key "eid|date" while patching
  const [blackout, setBlackout] = useState(null); // {from_date, reason, set_by, set_at} | null

  const load = useCallback(() => {
    api.get(`/hr/schedule?start_date=${start}&days=${days}`).then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/hr/attendance/blackout").then((r) => {
      setBlackout(r.data?.from_date ? r.data : null);
    }).catch(() => setBlackout(null));
  }, [start, days]);
  useEffect(() => { load(); }, [load]);

  const restoreRoster = async () => {
    if (!window.confirm("¿Restaurar el rol teórico? Volverán a mostrarse los días M/D calculados a partir del blackout.")) return;
    try {
      await api.delete("/hr/attendance/blackout");
      toast.success("Rol teórico restaurado");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const patchCell = async (row, dateIdx, status, opts = {}) => {
    const dateStr = data.dates[dateIdx];
    const key = `${row.employee_id}|${dateStr}`;
    setSaving(key);
    // Optimistic update
    setData((prev) => {
      if (!prev) return prev;
      const rows = prev.rows.map((r) => {
        if (r.employee_id !== row.employee_id) return r;
        const att = [...(r.attendance || [])];
        att[dateIdx] = status;
        const ov = [...(r.attendance_overrides || [])];
        ov[dateIdx] = opts.override_extra === undefined ? null : opts.override_extra;
        const sh = [...(r.attendance_shifts || [])];
        if (status === "present") {
          sh[dateIdx] = opts.shift || (r.shift_type || "day").toLowerCase();
        } else {
          sh[dateIdx] = null;
        }
        return { ...r, attendance: att, attendance_overrides: ov, attendance_shifts: sh };
      });
      return { ...prev, rows };
    });
    try {
      if (status === null) {
        await api.delete(`/hr/attendance/${row.employee_id}/${dateStr}`);
      } else {
        const payload = { status, note: "" };
        if (opts.shift) payload.shift = opts.shift;
        if (opts.override_extra !== undefined) payload.override_extra = opts.override_extra;
        await api.put(`/hr/attendance/${row.employee_id}/${dateStr}`, payload);
      }
      // Refresh to update extra_days + coverage
      load();
      const shiftLabel = opts.shift === "night" ? " (Noche)" : opts.shift === "day" ? " (Día)" : "";
      toast.success(`Asistencia actualizada: ${row.name} · ${dateStr}${shiftLabel}`);
    } catch (e) {
      toast.error(apiError(e));
      load();
    } finally { setSaving(null); }
  };

  if (!data) return <p className="text-sm text-slate-400 text-center py-16">Cargando…</p>;

  const activeCrews = Object.entries(data.coverage_by_crew || {})
    .filter(([, arr]) => arr.some((v) => v > 0)).map(([c]) => c);
  const crewColor = {};
  (data.crews || activeCrews).forEach((c, i) => { crewColor[c] = CREW_PALETTE[i % CREW_PALETTE.length]; });
  const chartDays = Math.min(days, 60);
  const chartData = data.dates.slice(0, chartDays).map((d, i) => {
    const row = { d: d.slice(5) };
    activeCrews.forEach((c) => { row[c] = data.coverage_by_crew[c]?.[i] || 0; });
    return row;
  });

  const renderCell = (r, i) => {
    const roster = r.on_site[i];
    const att = r.attendance?.[i];
    const overrideFlag = r.attendance_overrides?.[i];   // null | true | false
    // Iter101: shift real del día (guardado en hr_attendance). Si no viene,
    // se hereda del turno default del empleado.
    const cellShift = (r.attendance_shifts?.[i] || r.shift_type || "day").toLowerCase();
    if (att) {
      // Iter101: si presente, el letter depende del turno REGISTRADO ese día (D/N).
      const isNight = att === "present" && cellShift === "night";
      const baseKey = att === "present" && isNight ? "present_night" : att;
      const c = ATT_CELL[baseKey] || ATT_CELL[att] || { l: "?", cls: "bg-slate-300" };
      const isExtraByLogic = att === "present" && roster !== true;
      const countsAsExtra = isExtraByLogic && overrideFlag !== false;
      const shiftLabel = isNight ? "NOCHE" : "DÍA";
      if (isExtraByLogic && !countsAsExtra) {
        return {
          l: c.l,
          cls: `${c.cls} ring-2 ring-inset ring-slate-300`,
          title: `Presente ${shiftLabel} (extra descartado por admin — no se paga al doble). Click para cambiar`,
        };
      }
      if (countsAsExtra) {
        return {
          l: c.l,
          cls: `${c.cls} ring-2 ring-inset ring-fuchsia-400`,
          title: `Presente ${shiftLabel} (día fuera de rol → cuenta como extra pagado al doble). Click para excluir del pago extra`,
        };
      }
      return {
        ...c,
        title: att === "present"
          ? `Presente turno ${shiftLabel} — click para cambiar`
          : `Asistencia: ${att} — click para cambiar`,
      };
    }
    if (roster === true) return { l: "M", cls: "bg-emerald-500/30 text-emerald-900", title: "En mina (rol teórico) — click para registrar" };
    if (roster === false) return { l: "Ds", cls: "bg-slate-100 text-slate-400", title: "Descanso (rol teórico) — click para registrar" };
    return { l: "-", cls: "bg-white text-slate-300", title: "Sin ciclo — click para registrar" };
  };

  return (
    <div className="space-y-4" data-testid="hr-calendar">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="overline-label">Desde</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-40" data-testid="hr-schedule-start" /></div>
        <div><Label className="overline-label">Días</Label>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="h-9 w-24" data-testid="hr-schedule-days"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{[7, 14, 21, 30, 60, 90, 180, 365].map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent>
          </Select></div>
        {canManage && <ViaticosDialog />}
        {canManage && <BulkFillDialog start={start} days={days} onDone={load} />}
        {canManage && <BulkClearDialog start={start} days={days} onDone={load} />}
        {canManage && <ExcelTemplateButton start={start} days={days} />}
        {canManage && <ExcelImportButton onDone={load} />}
        <AnalysisByCrewButton start={start} days={days} />
        {data.total_extra_days > 0 && (
          <span className="text-xs font-bold text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 rounded-full px-3 py-1.5" data-testid="hr-extra-days-badge">
            {data.total_extra_days} día(s) extra en la ventana
          </span>
        )}
      </div>

      {blackout && (
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded p-3 flex items-center justify-between gap-3" data-testid="hr-cal-blackout-banner">
          <div className="text-xs text-amber-900">
            <p className="font-bold uppercase tracking-widest text-[10px] text-amber-700 mb-0.5">Rol teórico oculto</p>
            <p>
              Desde <b>{blackout.from_date}</b> — el calendario se muestra en blanco a partir de esta fecha
              hasta que se restaure el rol o se marquen asistencias manuales.
            </p>
            {blackout.reason && <p className="text-[11px] italic text-amber-800 mt-0.5">Motivo: {blackout.reason} · {blackout.set_by}</p>}
          </div>
          {canManage && (
            <Button size="sm" variant="outline" onClick={restoreRoster}
                    className="border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0"
                    data-testid="hr-cal-blackout-restore-btn">
              Restaurar rol teórico
            </Button>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-3 overflow-x-auto" data-testid="hr-schedule-coverage">
        <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">
          Cobertura por cuadrilla (empleados en mina/día){days > 60 ? " · primeros 60 días" : ""}
        </p>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="d" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <ChartTooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {activeCrews.map((c) => <Bar key={c} dataKey={c} stackId="a" fill={crewColor[c]} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="text-xs" data-testid="hr-schedule-table">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 z-10 min-w-[180px]">Colaborador</th>
              <th className="text-center px-2 py-2 sticky left-[180px] bg-slate-50 z-10">Cuadr.</th>
              <th className="text-center px-2 py-2 sticky left-[236px] bg-slate-50 z-10 text-fuchsia-700">Extra</th>
              {data.dates.map((d) => (
                <th key={d} className="text-center px-1 py-2 min-w-[34px]">
                  <span className="block font-normal text-[10px] text-slate-400">{d.slice(5, 7)}/{d.slice(8, 10)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.rows.map((r) => (
              <tr key={r.employee_id} data-testid={`hr-schedule-row-${r.employee_id}`}>
                <td className="px-3 py-1.5 sticky left-0 bg-white">
                  <p className="font-semibold text-slate-800">{r.name}</p>
                  <p className="text-[10px] text-slate-400">{r.position} · {r.regime}</p>
                </td>
                <td className="text-center sticky left-[180px] bg-white">
                  <span className="inline-block h-5 min-w-5 px-1 rounded-full bg-blue-100 text-blue-900 text-[10px] font-bold leading-5">{r.crew}</span>
                </td>
                <td className="text-center sticky left-[236px] bg-white">
                  {r.extra_days > 0 ? <span className="text-fuchsia-700 font-bold" data-testid={`hr-extra-count-${r.employee_id}`}>{r.extra_days}</span> : <span className="text-slate-300">0</span>}
                </td>
                {data.dates.map((_, i) => {
                  const c = renderCell(r, i);
                  const dateStr = data.dates[i];
                  const key = `${r.employee_id}|${dateStr}`;
                  const isBusy = saving === key;
                  const hasAtt = r.attendance?.[i] != null;
                  if (!canManage) {
                    return <td key={i} title={c.title} className={`text-center py-1.5 ${c.cls}`}>{c.l}</td>;
                  }
                  return (
                    <td key={i} className="p-0">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            title={c.title}
                            data-testid={`hr-cal-cell-${r.employee_id}-${i}`}
                            disabled={isBusy}
                            className={`w-full text-center py-1.5 transition ${c.cls} hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isBusy ? "opacity-60 animate-pulse" : ""}`}
                          >
                            {c.l}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="center" className="w-56 bg-white p-2" data-testid={`hr-cal-popover-${r.employee_id}-${i}`}>
                          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                            {r.name} · {dateStr}
                          </p>
                          <div className="space-y-0.5">
                            {ATT_LIST.map((s) => (
                              <button
                                key={s.v}
                                onClick={() => patchCell(r, i, s.status, s.shift ? { shift: s.shift } : {})}
                                data-testid={`hr-cal-set-${r.employee_id}-${i}-${s.v}`}
                                className="w-full text-left text-xs px-2 py-1 rounded hover:bg-slate-50 flex items-center gap-2 font-medium text-slate-700"
                              >
                                <span className={`inline-block h-2.5 w-2.5 rounded-full ${s.dot}`} />
                                {s.l}
                              </button>
                            ))}
                            {/* Toggle extra pay — solo relevante cuando el día
                                está marcado present sobre un rol OFF */}
                            {r.attendance?.[i] === "present" && r.on_site[i] !== true && (() => {
                              const currentOverride = r.attendance_overrides?.[i];
                              const countsAsExtra = currentOverride !== false;
                              const existingShift = (r.attendance_shifts?.[i] || r.shift_type || "day").toLowerCase();
                              return (
                                <button
                                  onClick={() => patchCell(r, i, "present", { shift: existingShift, override_extra: countsAsExtra ? false : true })}
                                  data-testid={`hr-cal-toggle-extra-${r.employee_id}-${i}`}
                                  className={`w-full text-left text-[11px] px-2 py-1 rounded border-t mt-1 pt-1.5 ${countsAsExtra ? "hover:bg-slate-50 text-slate-700" : "hover:bg-fuchsia-50 text-fuchsia-700"}`}
                                >
                                  {countsAsExtra
                                    ? "✂ Excluir del pago extra (no contar como día doble)"
                                    : "↺ Volver a contar como día extra"}
                                </button>
                              );
                            })()}
                            {hasAtt && (
                              <button
                                onClick={() => patchCell(r, i, null)}
                                data-testid={`hr-cal-clear-${r.employee_id}-${i}`}
                                className="w-full text-left text-[11px] px-2 py-1 rounded hover:bg-red-50 text-red-600 border-t mt-1 pt-1.5"
                              >
                                Borrar registro (usar rol teórico)
                              </button>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-[11px] text-slate-600 px-3 py-2 leading-relaxed">
          <span className="font-semibold text-slate-700">Rol teórico:</span> <b>M</b> = en mina · <b>Ds</b> = descanso (rol) · − = sin ciclo &nbsp;·&nbsp;
          <span className="font-semibold text-slate-700">Asistencia real:</span>
          <span className="font-bold text-emerald-700"> D</span> presente turno día ·
          <span className="font-bold text-indigo-700"> N</span> presente turno noche ·
          <span className="font-bold text-red-600"> A</span> ausente ·
          <span className="font-bold text-amber-600"> J</span> justificado ·
          <span className="font-bold text-slate-700"> Ds</span> descanso (real) ·
          <span className="font-bold text-blue-600"> V</span> vacaciones ·
          <span className="font-bold text-purple-600"> Pe</span> permiso ·
          <span className="font-bold text-orange-600"> Md</span> médico ·
          <span className="text-fuchsia-700 font-bold"> D◧/N◧</span> presente-extra (día fuera de rol pagado al doble)
          {canManage && <span className="block mt-1 italic text-slate-500">Tip: haz click en cualquier celda para editarla directamente.</span>}
        </p>
      </div>
    </div>
  );
}

function AnalysisByCrewButton({ start, days }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [from, setFrom] = useState(start);
  const [to, setTo] = useState(() => {
    const d = new Date(start);
    d.setDate(d.getDate() + Number(days || 30) - 1);
    return d.toISOString().slice(0, 10);
  });

  const download = async () => {
    if (!from || !to) { toast.error("Selecciona un rango válido"); return; }
    if (new Date(to) < new Date(from)) { toast.error("La fecha final debe ser ≥ inicial"); return; }
    setBusy(true);
    try {
      await downloadFile(
        `/hr/analysis-by-crew/pdf?date_from=${from}&date_to=${to}`,
        `analisis-por-cuadrilla-${from}-a-${to}.pdf`,
      );
      toast.success("PDF descargado");
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="hr-analysis-crew-btn"
          className="gap-1.5 border-purple-300 text-purple-800 hover:bg-purple-50"
        >
          <FilePdf size={14} weight="bold" /> Análisis por Cuadrilla
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white sm:max-w-md" data-testid="hr-analysis-crew-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Análisis por Cuadrilla · PDF</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">
          Genera un PDF ejecutivo agrupado por Departamento → Cuadrilla → Categoría con conteos
          D/N/Ds/A/J/V/Pe/Md, extras y % de asistencia en el rango seleccionado.
        </p>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <div>
            <Label className="overline-label">Desde</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                    data-testid="hr-analysis-from" />
          </div>
          <div>
            <Label className="overline-label">Hasta</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                    data-testid="hr-analysis-to" />
          </div>
        </div>
        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="hr-analysis-cancel">
            Cancelar
          </Button>
          <Button onClick={download} disabled={busy}
                   className="bg-purple-700 hover:bg-purple-800 gap-1.5"
                   data-testid="hr-analysis-download">
            <FilePdf size={14} weight="bold" /> {busy ? "Generando…" : "Descargar PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------ Excel template + import (Plantilla / Importar) ------------
function ExcelTemplateButton({ start, days }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      await downloadFile(
        `/hr/schedule/excel-template?start_date=${start}&days=${days}&prefill=true`,
        `calendario_hr_${start}_${days}d.xlsx`,
      );
      toast.success("Plantilla descargada");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={download}
      disabled={busy}
      data-testid="hr-cal-excel-template-btn"
      className="gap-1.5 border-blue-300 text-blue-800 hover:bg-blue-50"
    >
      <DownloadSimple size={14} weight="bold" /> {busy ? "Generando…" : "Plantilla Excel"}
    </Button>
  );
}

function ExcelImportButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/hr/schedule/import", fd);
      const errs = (data?.errors || []).length;
      toast.success(
        `Importado: ${data.upserts} celda(s) actualizadas${errs ? ` · ${errs} error(es)` : ""}`,
      );
      if (errs) {
        console.warn("Errores de importación:", data.errors);
      }
      onDone?.();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        data-testid="hr-cal-excel-import-btn"
        className="gap-1.5 border-orange-300 text-orange-800 hover:bg-orange-50"
      >
        <UploadSimple size={14} weight="bold" /> {busy ? "Subiendo…" : "Importar Excel"}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={upload}
        data-testid="hr-cal-excel-import-file"
      />
    </>
  );
}

// ------------ Bulk fill dialog (boton "Completar rol") ------------
function BulkFillDialog({ start, days, onDone }) {
  const [open, setOpen] = useState(false);
  const [forceStatus, setForceStatus] = useState("_auto");   // "_auto" | ATT_LIST[i].v
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const body = { start_date: start, days, only_missing: onlyMissing };
      if (forceStatus !== "_auto") body.force_status = forceStatus;
      const { data } = await api.post("/hr/attendance/bulk-fill", body);
      toast.success(`Completado: ${data.filled} celda(s) rellenadas (${data.skipped} ya tenian marca)`);
      onDone?.();
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid="hr-cal-bulk-fill-btn">
          <MagicWand size={14} weight="bold" /> Completar calendario
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md" data-testid="hr-cal-bulk-fill-dialog">
        <DialogHeader><DialogTitle className="font-heading">Completar calendario</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-slate-500">
            Rellena las celdas vacias segun el rol teorico (M {"->"} Presente, D {"->"} Descanso) o
            con un estado fijo. Se aplica del <b>{start}</b> por <b>{days}</b> dias.
          </p>
          <div>
            <Label className="overline-label">Estado a asignar</Label>
            <Select value={forceStatus} onValueChange={setForceStatus}>
              <SelectTrigger className="h-9" data-testid="hr-cal-bulk-fill-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="_auto">Automatico (segun rol teorico)</SelectItem>
                {ATT_LIST.map((s) => (<SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={onlyMissing} onCheckedChange={setOnlyMissing} data-testid="hr-cal-bulk-fill-only-missing" />
            <span className="text-xs">Solo celdas vacias (no sobrescribir marcas existentes)</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={run} disabled={busy} className="bg-emerald-700 hover:bg-emerald-600" data-testid="hr-cal-bulk-fill-run">
            {busy ? "Rellenando..." : "Completar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------ Bulk clear dialog (boton "Limpiar") ------------
function BulkClearDialog({ start, days, onDone }) {
  const [open, setOpen] = useState(false);
  const [onlyAuto, setOnlyAuto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  // Ventana editable — por default, se sincroniza con la vista actual del
  // calendario. El usuario puede ampliar o reducir el rango antes de borrar.
  const [rangeStart, setRangeStart] = useState(start);
  const [rangeEnd, setRangeEnd] = useState("");

  // Cuando cambia el rango visible del calendario, prellena. El usuario
  // conserva sus cambios manuales una vez que abre el dialog.
  useEffect(() => {
    if (!open) {
      setRangeStart(start);
      const s = new Date(start + "T00:00:00");
      s.setDate(s.getDate() + (days || 30) - 1);
      setRangeEnd(s.toISOString().slice(0, 10));
      setReason("");
    }
  }, [start, days, open]);

  const daysInRange = (() => {
    try {
      const a = new Date(rangeStart + "T00:00:00");
      const b = new Date(rangeEnd + "T00:00:00");
      const diff = Math.round((b - a) / 86400000) + 1;
      return diff > 0 ? diff : 0;
    } catch { return 0; }
  })();

  const cleanReason = (reason || "").trim();
  const reasonOk = cleanReason.length >= 8;

  const run = async () => {
    if (daysInRange <= 0) { toast.error("Rango inválido"); return; }
    if (daysInRange > 365) { toast.error("Máximo 365 días por operación"); return; }
    if (!reasonOk) { toast.error("Motivo obligatorio (mínimo 8 caracteres)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/hr/attendance/bulk-clear", {
        start_date: rangeStart, days: daysInRange, only_auto: onlyAuto,
        reason: cleanReason,
      });
      toast.success(`Limpieza: ${data.deleted} celda(s) borradas del ${rangeStart} al ${rangeEnd}`);
      onDone?.();
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50" data-testid="hr-cal-bulk-clear-btn">
          <Eraser size={14} weight="bold" /> Limpiar
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md" data-testid="hr-cal-bulk-clear-dialog"
                      aria-describedby="hr-cal-bulk-clear-desc">
        <DialogHeader>
          <DialogTitle className="font-heading">Limpiar calendario · rango de fechas</DialogTitle>
          <p id="hr-cal-bulk-clear-desc" className="text-xs text-slate-500">
            Borra las asistencias registradas en el rango indicado. El rol teórico
            (M/D) se conserva; solo se eliminan las marcas guardadas en la BD.
          </p>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Desde</Label>
              <Input type="date" value={rangeStart}
                      onChange={(e) => setRangeStart(e.target.value)}
                      data-testid="hr-cal-bulk-clear-start" />
            </div>
            <div>
              <Label>Hasta</Label>
              <Input type="date" value={rangeEnd}
                      onChange={(e) => setRangeEnd(e.target.value)}
                      min={rangeStart}
                      data-testid="hr-cal-bulk-clear-end" />
            </div>
          </div>
          <p className="text-[11px] text-slate-500" data-testid="hr-cal-bulk-clear-count">
            Ventana: <b className="text-slate-800">{daysInRange}</b> día(s) — máximo 365.
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={onlyAuto} onCheckedChange={setOnlyAuto} data-testid="hr-cal-bulk-clear-only-auto" />
            <span className="text-xs">Solo las que puso <b>Completar calendario</b> (más seguro)</span>
          </label>
          <div>
            <div className="flex items-center justify-between">
              <Label className={`text-[10px] uppercase tracking-widest font-bold ${reasonOk ? "text-slate-500" : "text-red-700"}`}>
                Motivo <span className="text-red-600">*</span>
                {!reasonOk && <span className="ml-1 normal-case font-normal">(obligatorio)</span>}
              </Label>
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${reasonOk ? "text-emerald-700 bg-emerald-50" : "text-red-700 bg-red-100 font-bold"}`}
                     data-testid="hr-cal-bulk-clear-counter">
                {cleanReason.length}/8 mín.
              </span>
            </div>
            <Textarea value={reason}
                      onChange={(e) => setReason(e.target.value.slice(0, 400))}
                      placeholder="Ej: reprogramación de rol, error masivo de captura…"
                      autoFocus
                      className={`mt-1 min-h-[60px] text-sm ${reasonOk ? "" : "border-red-300 focus-visible:ring-red-400"}`}
                      data-testid="hr-cal-bulk-clear-reason" />
            {!reasonOk && (
              <p className="text-[11px] text-red-700 mt-1 font-semibold flex items-center gap-1"
                  data-testid="hr-cal-bulk-clear-reason-hint">
                ↑ Escribe al menos {8 - cleanReason.length} carácter{(8 - cleanReason.length) !== 1 ? "es" : ""} más para habilitar el botón <b>Limpiar</b>
              </p>
            )}
          </div>
          {!onlyAuto && (
            <p className="text-[11px] text-red-600 font-semibold border-l-2 border-red-400 pl-2">
              ⚠ Se borrarán TODAS las marcas manuales del rango — esta acción queda registrada en el historial de auditoría.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={run} disabled={busy || daysInRange <= 0 || !reasonOk}
                   title={!reasonOk
                     ? `Escribe el motivo (mínimo 8 caracteres) — faltan ${8 - cleanReason.length}`
                     : daysInRange <= 0 ? "Rango inválido" : `Borrar ${daysInRange} día(s)`}
                   className="bg-red-600 hover:bg-red-500 disabled:opacity-100 disabled:bg-red-300 disabled:cursor-not-allowed"
                   data-testid="hr-cal-bulk-clear-run">
            {busy ? "Borrando..." : `Limpiar ${daysInRange} día(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
